import { useMemo, useState } from 'react';
import DataGrid, {Column, DataGridTypes, Export, Grouping, GroupPanel, Pager, Paging, Scrolling} from "devextreme-react/data-grid";
import { Workbook } from "exceljs";
import { saveAs } from 'file-saver-es';
import { exportDataGrid } from "devextreme/excel_exporter";
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import Icon from '../icon';

import './msiDatagrid.css'

const normalizeSearch = (value: unknown) => String(value ?? '')
  .toLocaleLowerCase('id-ID')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim();

const getColumnWidth = (column: string, numeric = false) => {
  if (numeric || /(^|_)(qty|jumlah|saldo|stok|stock|masuk|keluar|harga|total|nilai|berat)(_|$)/i.test(column)) return 140;
  if (/(nama|keterangan|deskripsi|description|alamat|jenis)/i.test(column)) return 240;
  if (/(tanggal|tgl|status|satuan|kode|sku|id)$/i.test(column)) return 150;

  return 180;
};

export interface ActionColumn {
  key: string;
  flex?: number;
  sortable?: boolean;
  minWidth?: number;
  field?: string;
  align?: "left" | "right" | "center" | string;
  headerAlign?: "left" | "right" | "center" | string;
  headerName?: string;
  caption?: string;
  renderCell: (params: { row: any; data?: any }) => React.ReactNode;
}
interface CustomRender {
  key: string;
  renderCell: (cellData: any) => React.ReactNode;
}
interface Props {
  array: any[];
  handleSelection?: any;
  rowClick?: any;
  cellClick?: any;
  toolbarPreparing?: any;
  formRender?: any[];
  customRender?: CustomRender[];
  hiddenColumns?: string[];
  convertNumeric?: string[];
  convertRupiah?: string[];
  columnOrder?: string[];
  columnCaptions?: Record<string, string>;
  actions?: ActionColumn[];
  actionsCaption?: string;
  groupPanel?: boolean;
  loading?: boolean;
  title?: string;
  searchPlaceholder?: string;

  onRowPrepared?: any;
  rowStyleRules?: {
    field: string;
    operator?: "==" | "!=" | ">" | "<" | ">=" | "<=";
    value: any;
    color: string;
  }[]
};

export default function MsiDatagrid(props: Props) {
  const {
    array,
    handleSelection,
    rowClick,
    cellClick,
    toolbarPreparing,
    customRender = [],
    formRender = [],
    hiddenColumns = [],
    convertNumeric = [],
    convertRupiah = [],
    columnOrder = [],
    columnCaptions = {},
    actions = [],
    actionsCaption,
    groupPanel = true,
    loading = false,
    title = 'Data',
    searchPlaceholder = 'Cari di semua kolom...',
    rowStyleRules = [],

    onRowPrepared: propsOnRowPrepared,
  } = props;

  const [searchText, setSearchText] = useState('');

  const sourceRows = Array.isArray(array) ? array : [];
  const filteredRows = useMemo(() => {
    const searchTokens = normalizeSearch(searchText).split(/\s+/).filter(Boolean);
    if (!searchTokens.length) return sourceRows;

    return sourceRows.filter(row => {
      const searchable = normalizeSearch(
        Object.entries(row ?? {})
          .filter(([key]) => !hiddenColumns.includes(key) && !key.startsWith('_'))
          .map(([, value]) => typeof value === 'object' ? JSON.stringify(value) : value)
          .join(' ')
      );

      return searchTokens.every(token => searchable.includes(token));
    });
  }, [array, searchText, hiddenColumns]);

  const hasUniqueId = filteredRows.length > 0
    && filteredRows.every(row => row?.id !== undefined && row?.id !== null)
    && new Set(filteredRows.map(row => String(row.id))).size === filteredRows.length;

  // ambil key dari data pertama
  const columns = Array.from(new Set([
    ...columnOrder,
    ...(array.length ? Object.keys(array[0]) : [])
  ])).filter(key => key !== 'ID');

  const toCaption = (text: string) => {
  if (!text) return '';

  return text
    // ganti underscore jadi spasi
    .replace(/_/g, ' ')
    // kasih spasi sebelum huruf besar (camelCase / PascalCase)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // lowercase semua dulu biar rapi
    .toLowerCase()
    // kapital huruf awal tiap kata
    .replace(/\b\w/g, char => char.toUpperCase());
  };

  const onExporting = (e: DataGridTypes.ExportingEvent) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Main sheet');

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
      });
    });
  };

  const pageSizes = [10, 25, 50, 100];

  const onRowPrepared = (e: DataGridTypes.RowPreparedEvent) => {
    if (propsOnRowPrepared) {
      propsOnRowPrepared(e);
      // continue to apply custom rules as well
    }

    if (e.rowType !== "data" || !rowStyleRules?.length) return;

    for (const rule of rowStyleRules) {
      const rowValue = e.data?.[rule.field];
      const compare = (a: any, b: any, op: string) => {
        switch (op) {
          case "==":
            return a == b;
          case "!=":
            return a != b;
          case ">":
            return a > b;
          case "<":
            return a < b;
          case ">=":
            return a >= b;
          case "<=":
            return a <= b;
          default:
            return false;
        }
      };
      if (compare(rowValue, rule.value, rule.operator ?? "==")) {
        if (e.rowElement) {
          e.rowElement.style.backgroundColor = rule.color;
          e.rowElement.style.color = "black";
        }
      }
    }
  };

  const formatRupiah = (value) => {
    return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0
    }).format(value || 0)
  }

  const formatNumber = (value) => {
    return new Intl.NumberFormat('id-ID').format(value || 0)
  }

  return (
    <Box className="msi-grid-shell">
      <Box className="msi-grid-toolbar">
        <Box>
          <Typography variant="subtitle2" fontWeight={800}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">
            {loading ? 'Memuat data...' : `${filteredRows.length} dari ${sourceRows.length} baris`}
          </Typography>
        </Box>
        <TextField
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder={searchPlaceholder}
          size="small"
          className="msi-grid-search"
          inputProps={{ 'aria-label': 'Cari data tabel' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon="mdi:magnify" width={20} />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchText('')} aria-label="Hapus pencarian">
                  <Icon icon="mdi:close" width={18} />
                </IconButton>
              </InputAdornment>
            ) : undefined
          }}
        />
      </Box>

      {loading ? (
        <Box className="msi-grid-loading" aria-label="Memuat tabel">
          <Skeleton variant="rounded" height={44} />
          {[0, 1, 2, 3, 4].map(row => <Skeleton key={row} height={42} />)}
        </Box>
      ) : (
      <DataGrid
      id="id"
      dataSource={filteredRows}
      keyExpr={hasUniqueId ? "id" : undefined}
      noDataText={searchText ? 'Data yang dicari tidak ditemukan' : 'Belum ada data untuk ditampilkan'}
      showBorders={false}
      allowColumnResizing={true}
      allowColumnReordering={true}
      showColumnLines={false}
      showRowLines={true}
      rowAlternationEnabled={true}
      hoverStateEnabled={true}
      wordWrapEnabled={false}
      columnResizingMode={'widget'}
      columnMinWidth={50}
      columnAutoWidth={false}
      // defaultColumns={columns}
      onSelectionChanged={handleSelection}
      onRowClick={rowClick}
      onCellClick={cellClick}
      onToolbarPreparing={(e) => {
        if (toolbarPreparing) toolbarPreparing(e);
      }}
      width="100%"
      scrolling={{ mode: 'standard' }}
      onExporting={onExporting}
      onRowPrepared={onRowPrepared}
    >
      {actions
        .map(act => (
          <Column
            key={act.key}
            dataField={act.field ?? act.key}
            caption={act.headerName ?? act.caption ?? actionsCaption ?? "ACTIONS"}
            alignment={(act.align as any) || "left"}
            minWidth={act.minWidth}
            allowSorting={act.sortable ?? false}
            cellRender={({ data }: any) => act.renderCell({ row: data, data })}
            allowEditing={false}
            // don't set width so columnAutoWidth can decide
          />
      ))}
      {columns
  .filter(col => !hiddenColumns.includes(col))
  .map(col => {
    const custom = customRender.find(c => c.key === col)

    const isNumeric = convertNumeric?.includes(col)
    const isRupiah = convertRupiah?.includes(col)

    return (
      <Column
        key={col}
        dataField={col}
        caption={columnCaptions[col] ?? toCaption(col)}
        width={getColumnWidth(col, isNumeric || isRupiah)}
        allowGrouping={true}
        allowResizing={true}
        allowSorting={true}
        cellRender={(cellData: any) => {
          // ✅ PRIORITAS 1: custom render
          if (custom) {
            return (
              <div className="msi-cell-one-line" title={String(cellData.value ?? '')}>
                {custom.renderCell(cellData)}
              </div>
            )
          }

          const value = cellData.value

          // ✅ PRIORITAS 2: rupiah
          if (isRupiah) {
            return <span className="msi-cell-one-line" title={formatRupiah(value)} style={{ textAlign: 'right' }}>
              {formatRupiah(value)}
            </span>
          }

          // ✅ PRIORITAS 3: numeric biasa
          if (isNumeric) {
            return <span className="msi-cell-one-line" title={formatNumber(value)} style={{ textAlign: 'right' }}>{formatNumber(value)}</span>
          }

          // ✅ DEFAULT
          return <span className="msi-cell-one-line" title={String(value ?? '')}>{value}</span>
        }}
      />
    )
  })}

      <Export enabled={true} />
      <Pager visible={true} allowedPageSizes={pageSizes} showPageSizeSelector={true} />
      <Paging defaultPageSize={10} />
      <GroupPanel visible={groupPanel} allowColumnDragging={true} />
      <Grouping autoExpandAll={true} allowCollapsing={true} />
    </DataGrid>
      )}
    </Box>
  );
}
